import { useDependencyInjector, useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import React, { useCallback, useMemo, useRef } from "react";
import styles from "./GroupsTab.module.scss";
import { observer } from "mobx-react-lite";
import { GroupsTabStore } from "../../stores/GroupsTabStore";
import type { ChatGroupType } from "@whatslly/types/chat-groups/ChatGroupType";
import type { GetGroupType } from "../../../ChatGroupsComponent/types/GetGroupType";
import { Messages } from "../../components/Messages";
import classnames from "classnames";
import { SearchMessagesSection } from "../../components/SearchMessagesSection";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { ScrollToBottomButton } from "../../components/ScrollToBottomButton";
import { Footer } from "../../components/Footer";
import { useTranslation } from "react-i18next";
import { LOADER_IMG } from "@whatslly/commons/constants";
import { ChatMessagesStore } from "../../stores/ChatMessagesStore";
import { MessagesTabsLocalStore } from "../../stores/MessagesTabsLocalStore";
import { Participants } from "../../../ChatGroupsComponent/pages/ChatGroup/components/Participants";
import { SearchInput } from "@whatslly/ui-components/v2/BaseElements/SearchInput";
import searchBarStypes from "../../components/SearchMessagesSection/search-messages-section.module.scss";
import get from "lodash/get";
import lowerCase from "lodash/lowerCase";
import { Highlighted } from "@whatslly/ui-components/Highlighted";
import { ErrorBox } from "@whatslly/ui-components/ErrorBox";
import { ChatDateFilter } from "../../../components/ChatDateFilter";
import { Dayjs } from "dayjs";
import { MenuDownloadButton } from "../components/menu-download-button";

export const GroupsTab: React.FC<{
  allowedToDownload?: boolean;
  hideDateFilter?: boolean;
  hideParticipants?: boolean;
  GroupFilterComponent?: React.FC;
  enableDateRangeFilter?: boolean;
  isSidecarsComponent?: boolean;
}> = observer(({ hideDateFilter, hideParticipants, GroupFilterComponent, enableDateRangeFilter, allowedToDownload, isSidecarsComponent }) => {
  const store = useDependencyInjector(GroupsTabStore);
  const chatMessagesStore = useDependencyInjector(ChatMessagesStore);
  const { t } = useTranslation();
  const groupRes = useMemo(() => {
    const state = chatMessagesStore.group.state;
    return {
      group: state !== "rejected" ? chatMessagesStore.group.value?.group : undefined,
      error: state === "rejected" ? chatMessagesStore.group.value : undefined,
    };
  }, [chatMessagesStore.group.state, chatMessagesStore.group.value]);
  return (
    <>
      {chatMessagesStore.selectedGroup ? (
        <GroupComponent
          hideDateFilter={hideDateFilter}
          group={groupRes.group}
          GroupFilterComponent={GroupFilterComponent}
          enableDateRangeFilter={enableDateRangeFilter}
          allowedToDownload={allowedToDownload}
          error={groupRes.error}
          isSidecarsComponent={isSidecarsComponent}
        />
      ) : (
        <>
          <div className={searchBarStypes.searchAndActionsContainer}>
            <SearchInput
              data-testid={`search-group-input`}
              placeholder={t("chat_component.search_group_placeholder")}
              className={searchBarStypes.searchInput}
              onChange={(newSearchTerm) => store.setSearchTerm(newSearchTerm)}
              clearAllContainerClassName={searchBarStypes.clearAllContainer}
              value={store.searchTerm}
              isDisabled={chatMessagesStore.groups.state !== "fulfilled"}
            />
          </div>

          {chatMessagesStore.groups.case({
            fulfilled: (groups) => (
              <GroupsTable
                hideParticipants={hideParticipants}
                searchTerm={store.searchTerm}
                groups={groups.groups.filter((group) => group.name.toLowerCase().includes(store.searchTerm.toLowerCase()))}
              />
            ),
            pending: () => (
              <div className={styles.placeHolderGroups}>
                <img src={LOADER_IMG} alt="loader" />
                <div>{t("chat_component.loading")}</div>
              </div>
            ),
            rejected: (error) => (
              <div className={classnames(styles.placeHolderGroups)}>
                <SvgV2 width={20} height={20} iconName="unknown-issue" />
                <div>{error.message}</div>
              </div>
            ),
          })}
        </>
      )}
    </>
  );
});

const NoResults: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div data-testid="group-not-results" className={classnames(styles.noResults)}>
      <div>{t("chat_groups.no_results_found")}</div>
      <div>
        {t("chat_groups.try_different_keywords")}
        <div>{t("chat_groups.spelled_correctly")}</div>
      </div>
    </div>
  );
};

const GroupsTable: React.FC<{ groups: ChatGroupType[]; searchTerm: string; hideParticipants?: boolean }> = observer(
  ({ groups, searchTerm, hideParticipants }) => {
    const chatMessagesStore = useDependencyInjector(ChatMessagesStore);

    const { t } = useTranslation();

    if (groups.length === 0 && searchTerm) {
      return (
        <div className={styles.groupsTable}>
          <NoResults />
        </div>
      );
    }

    if (groups.length === 0 && !searchTerm) {
      return (
        <div data-testid="no-groups-error" className={styles.groupsTable}>
          <div className={styles.noGroups}>
            <div className={styles.noGroupsBackgroundImg} />
            <div>{t("chat_component.no_link")}</div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.groupsTable}>
        {groups.map((group) => {
          return (
            <div
              data-testid="groups-list-item"
              className={styles.groupInTable}
              onClick={() => chatMessagesStore.setSelectedGroup(group.Id)}
              key={group.Id}
            >
              <Highlighted text={group.name} highlight={searchTerm} className={styles.groupName} />
              {!hideParticipants && (
                <span className={styles.groupNameParticipants}>{` (${group.participants ?? 0} ${lowerCase(t("chat_groups.participants"))})`}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  }
);

const GroupComponent: React.FC<{
  allowedToDownload?: boolean;
  group?: GetGroupType;
  error?: any;
  hideDateFilter?: boolean;
  GroupFilterComponent?: React.FC;
  enableDateRangeFilter?: boolean;
  isSidecarsComponent?: boolean;
  openCalendarInTheMiddle?: boolean;
}> = observer(
  ({
    openCalendarInTheMiddle,
    group,
    error,
    hideDateFilter,
    GroupFilterComponent,
    enableDateRangeFilter,
    allowedToDownload,
    isSidecarsComponent,
  }) => {
    const scrollToBottomRef = useRef<{ scrollBottom: () => void } | null>(null);
    const groupsStore = useDependencyInjector(GroupsTabStore);
    const localStore = useLocalStoreWithInjectedDependencies(MessagesTabsLocalStore);
    const messagesStore = useDependencyInjector(ChatMessagesStore);
    const { t } = useTranslation();

    const setShowScrollDown = useCallback(
      (show: boolean) => {
        localStore.setShowScrollToBottom(show);
      },
      [localStore]
    );

    const dateRangeConfigs = useMemo(() => {
      if (!enableDateRangeFilter) return {};
      return {
        onDateRangeChange: (dateRange: { fromDate: Dayjs | null; toDate: Dayjs | null }) => messagesStore.setDateRange(dateRange),
        onFilterTypeChange: (mode: "byMonth" | "range") => messagesStore.setDateFilterMode(mode),
        dateRange: messagesStore.dateRange,
        openInMode: messagesStore.dateFilterMode,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableDateRangeFilter, messagesStore, messagesStore.dateRange]);

    const filteredMessages = useMemo(() => {
      const filters = groupsStore.getFilters();
      const searchKeys = Object.keys(filters);
      return (
        group?.messages.filter((item) => {
          return !searchKeys.some((key) => filters[key].includes(get(item, key)));
        }) || []
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [group?.messages, groupsStore.filters]);

    const selectAllMessages = useCallback(() => localStore.setSelectAllMessages("group"), [localStore]);

    return (
      <div className={styles.groupContainer}>
        <SearchMessagesSection
          isLoading={messagesStore.group.state !== "fulfilled"}
          messages={filteredMessages}
          onSearchTextChange={(text) => localStore.setSearchText(text)}
          onFocusMessageChange={(msgId) => localStore.setFocusMessageId(msgId)}
          elementBeforeSearch={
            <div
              data-testid="back-to-groups-list-button"
              onClick={() => {
                groupsStore.resetSelectedGroup();
                messagesStore.setSelectedGroup(undefined);
              }}
              className={styles.backContainer}
            >
              <SvgV2 width={16} height={14} iconName="arrow-right-short" />
            </div>
          }
        >
          <div className={styles.headerButtons}>
            {messagesStore.allowedToDelete && (
              <div
                data-testid="delete-group-message-button"
                onClick={() => localStore.toggleDeleteMode()}
                className={classnames(styles.headerButton, {
                  [styles.selected]: localStore.isDeleteMode,
                  [styles.disabled]: messagesStore.group.state !== "fulfilled",
                })}
              >
                <SvgV2 width={12} height={12} iconName="trash" />
              </div>
            )}
            {allowedToDownload && (
              <MenuDownloadButton
                isDownloadMode={localStore.isDownloadMode}
                toggleDownloadMode={localStore.toggleDownloadMode.bind(localStore)}
                disabled={messagesStore.messages.state !== "fulfilled" || messagesStore.userId.state !== "fulfilled"}
              />
            )}
            <div
              onClick={() => messagesStore.refreshGroup()}
              className={classnames(styles.headerButton, { [styles.disabled]: messagesStore.group.state !== "fulfilled" })}
            >
              <SvgV2 width={15} height={15} iconName="synchronize" />
            </div>
            {!hideDateFilter && (
              <ChatDateFilter
                isSideCarFilter={isSidecarsComponent}
                defaultValue={messagesStore.showXMonthsAgo}
                disabled={messagesStore.group.state !== "fulfilled"}
                onChange={(value) => messagesStore.setShowLastXMonthsTimeStamp(value)}
                openCalendarInTheMiddle={openCalendarInTheMiddle}
                {...dateRangeConfigs}
              />
            )}
            {group?.members && (
              <div
                data-testid="show-participants-button"
                onClick={() => groupsStore.setShowParticipants(!groupsStore.showParticipants)}
                className={classnames(styles.headerButton, {
                  [styles.disabled]: messagesStore.group.state !== "fulfilled",
                  [styles.selected]: groupsStore.showParticipants,
                })}
              >
                <SvgV2 width={15} height={15} iconName="list" />
              </div>
            )}
            {GroupFilterComponent && <GroupFilterComponent />}
          </div>
        </SearchMessagesSection>
        <div className={classnames(styles.participantAndMessagesContainer, { [styles.showParticipants]: groupsStore.showParticipants })}>
          {group?.members && (
            <div className={styles.participantsContainer}>
              <Participants menuItemNameSize={`small`} members={group?.members} loginProvider={messagesStore.crm} baseUrl={messagesStore.baseUrl} />
            </div>
          )}

          <div className={styles.messagesWrapper}>
            <div className={styles.messagesContainer}>
              {messagesStore.group.case({
                pending: () => (
                  <div className={styles.loadingData}>
                    <img src={LOADER_IMG} alt="loader" />
                    <div>{t("chat_component.loading")}</div>
                  </div>
                ),
                rejected: () => (
                  <div data-testid="error-load-group" className={styles.loadingData}>
                    <ErrorBox error={error?.message ?? t("error_try_again")} />
                  </div>
                ),
                fulfilled: () => (
                  <Messages
                    ref={scrollToBottomRef}
                    crm={messagesStore.crm}
                    items={filteredMessages}
                    tempDeletedMessages={localStore.tempDeletedMessages}
                    isSelectMessagesMode={localStore.isDeleteMode || localStore.isDownloadMode}
                    selectedMessages={localStore.selectedMessages}
                    searchText={localStore.searchText}
                    onChangeSelectMessageCheckbox={(messageId) => localStore.setSelectedMessages(messageId)}
                    highlightedMsgId={localStore.focusMessageId}
                    getEntityUrl={(name, id) => messagesStore.getEntityUrl(name, id)}
                    setHighlightedMessageId={(msgId) => localStore.setFocusMessageId(msgId)}
                    setShowScrollDown={setShowScrollDown}
                    hideMessageAck={true}
                  />
                ),
              })}
            </div>
            <ScrollToBottomButton
              className={styles.groupMessagesScrollDown}
              showScrollToBottom={localStore.showScrollToBottom}
              scrollToBottomRef={scrollToBottomRef}
            />
          </div>
        </div>

        {localStore.isDeleteMode && (
          <Footer
            mode={"delete"}
            closeDeleteMode={localStore.closeDeleteMode.bind(localStore)}
            numOfSelectedMessages={localStore.selectedMessages.size}
            selectAll={selectAllMessages}
            clickOnDeleteMessages={localStore.deleteMessages.bind(localStore)}
          />
        )}
        {localStore.isDownloadMode && (
          <Footer
            mode={"download"}
            closeDeleteMode={localStore.closeDownloadMode.bind(localStore)}
            numOfSelectedMessages={localStore.selectedMessages.size}
            selectAll={selectAllMessages}
            downloadMessages={localStore.downloadMessages.bind(localStore)}
          />
        )}
      </div>
    );
  }
);
